'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, Autoplay } from 'swiper/modules';
import { useState, useRef, useEffect } from 'react';
import { truncate } from 'lodash';
import {
    mapPropertyCategoryToText,
    mapPropertySubTypeToText,
    resolveListingSubtype,
} from '@/features/listings/utils';
import { cn } from '@/shadcn/lib/utils';
import { BREAKPOINTS } from '@/shared/constants';
import { Img } from '@/shared/components/ui/Img';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Listing } from '../types';
import { formatPrice } from '@/features/adverts/utils';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { ImagePlaceholder } from '@/shared/components/ui/ImagePlaceholder';

interface Props {
    // variant: 'default' | 'simple';
    withDelay?: number;
    background?: 'white' | 'grey';
    listings: Array<Listing>;
    maxSlidesPerView: 4 | 5 | 6;
}

const ACTION_STYLES = [
    'h-full absolute flex items-start top-[110px] z-[2] cursor-pointer',
    '[&>div]:size-[35px]',
    '[&>div]:bg-[#e8e8e8]',
    '[&>div]:rounded-full',
    '[&>div]:flex',
    '[&>div]:items-center',
    '[&>div]:justify-center',
    'sm:top-[78px]',
    'min-[1300px]:[&>div]:bg-transparent',
];

export function ListingsSlider(props: Props) {
    const [sliderInit, setSliderInit] = useState(false);
    const swiperRef: any = useRef(null);
    const paginationRef = useRef<HTMLDivElement | null>(null);
    const slidesPerView = props.maxSlidesPerView ?? 4;
    const [currentSlidesPerView, setCurrentSlidesPerView] = useState(1);

    useEffect(() => {
        let timer: any = 0;
        if (props.withDelay && swiperRef.current?.autoplay) {
            swiperRef.current.autoplay.stop();
            timer = setTimeout(() => {
                swiperRef.current.autoplay.start();
            }, props.withDelay);
        }

        return () => clearTimeout(timer);
    }, [props.withDelay, swiperRef.current]);

    const syncSlidesPerView = (swiper: any) => {
        setCurrentSlidesPerView(Number(swiper.params.slidesPerView) || 1);
    };

    const showNavigation = props.listings.length > currentSlidesPerView;

    const background = props.background === 'grey' ? 'grey' : 'white';

    return (
        <div>
            <div className={cn('flex justify-center items-center relative')}>
                {showNavigation && (
                    <div
                        className={cn(
                            ACTION_STYLES,
                            '-left-[17px]',
                            'sm:-left-[19px]',
                            'xl:-left-[25px]',
                            '2xl:-left-[35px]',
                        )}
                        onClick={() => {
                            if (swiperRef?.current) swiperRef.current.slidePrev();
                        }}
                    >
                        <div>
                            <ChevronLeftIcon className="size-[25px] -ml-[3px]" />
                        </div>
                    </div>
                )}
                <div className={cn('w-full')}>
                    <>
                        {props.listings.length > 0 && (
                            <Swiper
                                className={cn(
                                    '!pb-[70px]', // !px-[10px] !-mx-[20px] !overflow-visible
                                    // '[&_.swiper-wrapper]:!overflow-visible',
                                    // '[&_.swiper-slide]:!overflow-visible',
                                    // 'sm:!-mx-[30px] sm:!px-[10px]',
                                    // 'md:!-mx-[30px] md:!px-[30px]',
                                    background === 'grey'
                                        ? '[&_.swiper-wrapper]:bg-[#f9f9f9]'
                                        : '[&_.swiper-wrapper]:bg-[#fff]',
                                    background === 'grey' &&
                                        '[&_.swiper-wrapper]:bg-[#f9f9f9] [&_.swiper-wrapper]:rounded-[20px]',
                                )}
                                onSwiper={(swiper) => {
                                    swiperRef.current = swiper;
                                    syncSlidesPerView(swiper);
                                }}
                                onBreakpoint={(swiper) => {
                                    syncSlidesPerView(swiper);
                                }}
                                modules={[Navigation, Pagination, Scrollbar, Autoplay]}
                                spaceBetween={0}
                                slidesPerView={1}
                                breakpoints={{
                                    [BREAKPOINTS.sm]: {
                                        slidesPerView: 2,
                                        spaceBetween: 0,
                                    },
                                    [BREAKPOINTS.md]: {
                                        slidesPerView: slidesPerView - 2,
                                        spaceBetween: 0,
                                    },
                                    [BREAKPOINTS.lg]: {
                                        slidesPerView: slidesPerView - 1,
                                        spaceBetween: 0,
                                    },
                                    [BREAKPOINTS.xl]: {
                                        slidesPerView: slidesPerView,
                                        spaceBetween: 0,
                                    },
                                    [BREAKPOINTS.xxxl]: {
                                        slidesPerView: slidesPerView + 1,
                                        spaceBetween: 0,
                                    },
                                }}
                                loop={true}
                                autoplay={{
                                    delay: 300000,
                                    disableOnInteraction: false,
                                }}
                                //  onBeforeInit={(swiper) => {
                                //      if (
                                //          swiper.params.pagination &&
                                //          typeof swiper.params.pagination !== 'boolean'
                                //      ) {
                                //          swiper.params.pagination.el = paginationRef.current;
                                //     }
                                // }}
                                pagination={{
                                    clickable: true,
                                }}
                                onInit={() => setSliderInit(true)}
                                // style={sliderInit ? { height: 'auto' } : { height: '0' }}
                                style={
                                    {
                                        ...(sliderInit ? { height: 'auto' } : { height: '0' }),
                                        '--swiper-pagination-color': '#000',
                                        '--swiper-pagination-bullet-inactive-color': '#ccc',
                                        '--swiper-pagination-bullet-inactive-opacity': '1',
                                    } as React.CSSProperties
                                }
                            >
                                {props.listings.map((listing, i) => {
                                    const area =
                                        listing.detail?.builtUpArea ??
                                        listing.detail?.usableArea ??
                                        listing.detail?.landArea;
                                    const pricePerM2 =
                                        listing.finalPrice && area
                                            ? Math.round(listing.finalPrice / Number(area))
                                            : null;
                                    const imageSrc =
                                        listing.photo?.url ?? listing.gallery?.[0]?.url ?? '';
                                    const subtype = resolveListingSubtype(listing);
                                    const propertyTypeText = subtype
                                        ? mapPropertySubTypeToText(subtype)
                                        : mapPropertyCategoryToText(listing.propertyCategory);
                                    const location = [
                                        listing.municipality?.name,
                                        listing.district?.name,
                                    ]
                                        .filter(Boolean)
                                        .join(', ');

                                    return (
                                        <SwiperSlide
                                            key={i}
                                            style={sliderInit ? { opacity: '1' } : { opacity: '0' }}
                                            className={cn(
                                                '!h-auto',
                                                'px-[10px] pt-[10px] rounded-[15px]',
                                                'transition-all duration-300 ease-out',
                                                background === 'white' &&
                                                    'hover:shadow-[0px_15px_50px_rgba(0,0,0,0.08)]',
                                            )}
                                        >
                                            <a
                                                href={`/inzerat/${listing.id}/${listing.slug}`}
                                                target="_blank"
                                                className={cn(
                                                    background === 'grey'
                                                        ? 'bg-[#f9f9f9]'
                                                        : 'bg-[#fff]',
                                                    'w-full h-full p-[0px] rounded-[20px]',
                                                    'rounded-[15px]',
                                                    'transition-all duration-300 ease-out',
                                                    background === 'grey' &&
                                                        'sm:hover:bg-[#fff] sm:hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)]',
                                                    // background === 'white' &&
                                                    //     'hover:shadow-[0px_15px_50px_rgba(0,0,0,0.08)]',
                                                    //  'sm:p-[10px] sm:px-[10px]',
                                                )}
                                            >
                                                <div className="h-[230px] sm:h-[170px]">
                                                    <div className="rounded-[12px] h-full overflow-hidden xl:rounded-[15px]">
                                                        {imageSrc ? (
                                                            <Img
                                                                className={cn(
                                                                    'w-full h-full object-cover',
                                                                )}
                                                                src={imageSrc}
                                                                alt={listing.slogan}
                                                            />
                                                        ) : (
                                                            <ImagePlaceholder />
                                                        )}
                                                    </div>
                                                </div>
                                                <div
                                                    className={cn(
                                                        'p-[12px] py-[15px] text-[15px] flex flex-col gap-[3px]',
                                                        'sm:text-[12px]',
                                                        'lg:text-[13px]',
                                                        'xl:text-[14px]',
                                                    )}
                                                >
                                                    <h4
                                                        className={cn(
                                                            'text-[17px] font-[700]',
                                                            'sm:text-[14px]',
                                                            'lg:text-[15px]',
                                                            'xl:text-[16px]',
                                                        )}
                                                    >
                                                        {truncate(listing.slogan, { length: 32 })}
                                                    </h4>
                                                    <p>{location}</p>
                                                    <p>
                                                        {propertyTypeText}
                                                        {area ? ` ${area} m²` : ''}
                                                    </p>
                                                    <div
                                                        className={cn(
                                                            'flex items-end gap-[8px]',
                                                            'sm:gap-[5px]',
                                                        )}
                                                    >
                                                        <small
                                                            className={cn(
                                                                'font-[700] text-[18px]',
                                                                'sm:text-[15px]',
                                                                'md:text-[16px]',
                                                                'xl:text-[17px]',
                                                            )}
                                                        >
                                                            {listing.priceOnRequest
                                                                ? 'Cena dohodou'
                                                                : listing.finalPrice
                                                                  ? `${formatPrice(listing.finalPrice)} €`
                                                                  : null}
                                                        </small>
                                                        {pricePerM2 && (
                                                            <small
                                                                className={cn(
                                                                    'text-[13px] mb-[3px]',
                                                                    'sm:text-[11px]',
                                                                    'md:text-[12px]',
                                                                    'xl:text-[13px]',
                                                                )}
                                                            >
                                                                {formatPrice(pricePerM2)} € /m²
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                            </a>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        )}
                        <div
                            ref={paginationRef}
                            className="mt-4 flex items-center justify-center gap-2"
                        />
                    </>
                </div>
                {showNavigation && (
                    <div
                        className={cn(
                            ACTION_STYLES,
                            '-right-[17px]',
                            'sm:-right-[19px]',
                            'xl:-right-[30px]',
                            '2xl:-right-[35px]',
                        )}
                        onClick={() => {
                            if (swiperRef?.current) swiperRef.current.slideNext();
                        }}
                    >
                        <div>
                            <ChevronRightIcon className="size-[25px] -mr-[3px]" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
